import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Award,
  Activity,
  Hammer,
  ClipboardCheck,
  Headphones,
} from "lucide-react";

const CARDS = [
  {
    icon: ShieldCheck,
    title: "Owner Inspection",
    desc: "Every machine is personally inspected before dispatch.",
  },
  {
    icon: Award,
    title: "Premium Components",
    desc: "Only carefully selected branded components are used to ensure dependable performance and long service life.",
  },
  {
    icon: Activity,
    title: "Performance Tested",
    desc: "Every machine undergoes operational testing before delivery.",
  },
  {
    icon: Hammer,
    title: "Heavy Duty Engineering",
    desc: "Manufactured using robust structural designs and premium-grade materials.",
  },
  {
    icon: ClipboardCheck,
    title: "Third-Party Inspection Ready",
    desc: "Our machines are engineered to meet quality expectations for third-party inspection requirements.",
  },
  {
    icon: Headphones,
    title: "Reliable After-Sales Support",
    desc: "We continue supporting our customers long after installation through technical guidance and service assistance.",
  },
];

export default function QualityWithoutCompromise() {
  return (
    <section
      data-testid="about-quality"
      className="relative bg-liftile-black py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle orange ambient blobs */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[460px] h-[460px] rounded-full pointer-events-none opacity-25"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.45) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="heading-eyebrow mb-4">Engineering Standards</div>
          <h2
            data-testid="quality-heading"
            className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
          >
            Quality Without
            <br />
            <span className="text-liftile-orange">Compromise</span>
          </h2>
          <p className="font-body text-liftile-cream/65 mt-6 text-base md:text-lg leading-relaxed max-w-2xl">
            Quality is not inspected at the end.
            <br />
            It is engineered into every stage of manufacturing.
          </p>
        </motion.div>

        {/* 6 feature cards */}
        <div
          data-testid="quality-grid"
          className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, ease: "easeOut", delay: i * 0.08 }}
                data-testid={`quality-card-${i}`}
                className="group relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.07] rounded-2xl p-8 md:p-9 hover:bg-white/[0.07] hover:border-liftile-orange/55 hover:-translate-y-1 transition-all duration-400"
              >
                {/* Icon plate */}
                <div className="relative w-14 h-14 rounded-xl grid place-items-center text-liftile-orange border border-liftile-orange/40 mb-6 group-hover:bg-liftile-orange group-hover:text-white group-hover:border-liftile-orange transition-all duration-400">
                  <Icon className="w-6 h-6" strokeWidth={1.7} />
                </div>
                <h3 className="font-heading uppercase text-lg md:text-xl text-liftile-cream tracking-tight leading-snug">
                  {card.title}
                </h3>
                <div className="mt-3 w-10 h-0.5 bg-liftile-orange/70 transition-all duration-400 group-hover:w-16" />
                <p className="mt-4 font-body text-sm md:text-base text-liftile-cream/65 leading-relaxed">
                  {card.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
